/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-10-16 11:21:53
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import cloneDeep from "lodash-es/cloneDeep";
import { produce } from "immer";

import { usePersistStore } from "@/frameworks/core/persistence";
import { CallService } from "@/frameworks/core/API";
import type { FavoriteDispatch } from "./states";

const LOGTAG = "#Favorite:Actions#";

export default class FavoriteActions implements FavoriteDispatch {
	private static _instance: FavoriteActions | null = null;
	private constructor() {}
	public static get instance() {
		if (!FavoriteActions._instance) {
			FavoriteActions._instance = new FavoriteActions();
		}
		return FavoriteActions._instance;
	}

	/**
	 * Loads favorites either from the persisted state or from an external data source if the persisted state is empty.
	 * Updates the persisted state with the loaded favorites.
	 *
	 * @return {Promise<void>} A promise that resolves when the favorites are loaded and the state is updated.
	 */
	public async initialize(): Promise<void> {
		// const { favorites } = usePersistStore.getState();
		// if (favorites.length <= 0) {
		const callAPI = new CallService({});
		const { data } = await callAPI.callFavorite.inquiry.execute();
		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.favorites = data ?? [];
			})
		);
		// }
	}

	/**
	 * Checks if a given user is marked as a favorite.
	 *
	 * @param {string} userID - The unique identifier of the user to check.
	 * @return {boolean} - Returns true if the user is marked as a favorite, otherwise false.
	 */
	public is_favorite(userID: string): boolean {
		const { favorites } = usePersistStore.getState();
		if (favorites.length <= 0) {
			return false;
		}
		const { favorite_id } = this.find_favorite_id_by_userID(favorites, userID);
		return favorite_id !== null;
	}

	/**
	 * Marks a user as a favorite and updates the persisted store accordingly.
	 * Makes an asynchronous call to add the user to the favorites list in the backend.
	 * Updates locally stored favorite data upon successful response or reverts changes if an error occurs.
	 *
	 * @param {string} userID - The unique identifier of the user to be added to favorites.
	 * @return {Promise<void>} - A promise that resolves when the user's favorite status is updated.
	 */
	public async add(userID: string): Promise<void> {
		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.users[userID].callFavorite = true;
			})
		);

		const { data } = await this.add_favorite(userID).catch((error) => ({ data: null, error }));
		if (data) {
			usePersistStore.setState((state) =>
				produce(state, (draft) => {
					draft.favorites = data;
				})
			);
		} else {
			usePersistStore.setState((state) =>
				produce(state, (draft) => {
					draft.users[userID].callFavorite = false;
				})
			);
		}
	}

	/**
	 * Changes the order of a favorite item and updates the persistent store accordingly.
	 *
	 * @param {string} favoriteId - The unique identifier of the favorite item whose order is to be changed.
	 * @param {number} orderNumber - The new order number for the favorite item.
	 * @return {Promise<void>} - A promise that resolves when the order has been successfully changed and the favorites have been reloaded.
	 */
	public async changeOrder(favoriteId: string, orderNumber: number): Promise<void> {
		if (favoriteId) {
			this.changeOrder_favorite(favoriteId, orderNumber)
				.then(async () => {
					const callAPI = new CallService({});
					const { data } = await callAPI.callFavorite.inquiry.execute();
					usePersistStore.setState((state) =>
						produce(state, (draft) => {
							draft.favorites = data ?? [];
						})
					);
				})
				.catch(async (error) => {
					console.warn(LOGTAG, "Favorite change order failed with", error);
				});
		}
	}

	/**
	 * Deletes the favorite for a given user ID.
	 *
	 * @param {string} userID - The ID of the user whose favorite is to be deleted.
	 * @return {Promise<void>} A promise that resolves when the deletion process is completed.
	 */
	public async delete(userID: string): Promise<void> {
		const { favorites } = usePersistStore.getState();
		const { favorite_id, index } = this.find_favorite_id_by_userID(favorites, userID);
		if (favorite_id) {
			usePersistStore.setState((state) =>
				produce(state, (draft) => {
					draft.users[userID].callFavorite = false;
					const _temp = cloneDeep(draft.favorites);
					_temp.splice(index, 1);
					draft.favorites = _temp;
				})
			);
			this.remove_favorite(favorite_id, userID)
				.then(async ({ data }) => {
					console.debug(LOGTAG, "Favorite deleted", data);
				})
				.catch(async (error) => {
					console.warn(LOGTAG, "Favorite delete failed with", error);
					usePersistStore.setState((state) =>
						produce(state, (draft) => {
							draft.users[userID].callFavorite = false;
						})
					);
				});
		}
	}

	/**
	 * Finds the favorite ID and its index in the favorite list for a given user ID.
	 *
	 * @param favorite  The list of favorite data as provided by WebServices.Favorites.Inquiry.Data.
	 * @param userID    The user ID for which the favorite ID is to be found.
	 * @return          An object containing the favorite ID and its index. If the favorite ID is not found,
	 *                  favorite_id is null and index is -1.
	 * @private
	 */
	private find_favorite_id_by_userID(
		favorite: WebServices.Favorites.Inquiry.Data,
		userID: string
	): { favorite_id: string | null; index: number } {
		const filtered = favorite.filter((item) => item.userId === userID);
		const favorite_id = filtered && filtered.length > 0 ? filtered[0].id : null;
		const index = favorite.findIndex((item) => item.id === favorite_id);
		return { favorite_id, index };
	}

	/**
	 * Adds a user to the favorites list and retrieves the updated favorites list.
	 *
	 * @param {string} userID - The unique identifier of the user to add to the favorites.
	 * @return {Promise<WebServices.Favorites.Inquiry.Response>} The updated list of favorites.
	 * @private
	 */
	private async add_favorite(userID: string): Promise<WebServices.Favorites.Inquiry.Response> {
		const callAPI = new CallService({});
		await callAPI.callFavorite.add.execute({ targetUserId: userID });
		return await callAPI.callFavorite.inquiry.execute();
	}

	/**
	 * Changes the order of the favorite item identified by the given favorite ID.
	 *
	 * @param {string} favorite_id - The unique identifier of the favorite item whose order is to be changed.
	 * @param {number} orderNumber - The new order number for the favorite item.
	 * @return {Promise<WebServices.Favorites.ChangeOrder.Response>} A promise that resolves to the response of the change order operation.
	 * @private
	 */
	private async changeOrder_favorite(
		favorite_id: string,
		orderNumber: number
	): Promise<WebServices.Favorites.ChangeOrder.Response> {
		const callAPI = new CallService({});
		return await callAPI.callFavorite.changeOrder.execute({ callFavoritesId: favorite_id, orderNumber });
	}

	/**
	 * Removes a favorite item for a user.
	 *
	 * @param {string} favorite_id - The ID of the favorite item to remove.
	 * @param {string} userID - The ID of the user whose favorite item is to be removed.
	 * @return {Promise<WebServices.Favorites.Delete.Response>} A promise that resolves to the response of the delete operation.
	 * @private
	 */
	private async remove_favorite(favorite_id: string, userID: string): Promise<WebServices.Favorites.Delete.Response> {
		const callAPI = new CallService({});
		return await callAPI.callFavorite.remove.execute({
			callFavoritesId: favorite_id,
			targetUserId: userID
		});
	}

	/**
	 * Overrides the existing favorites in the persist store with the updated favorites data.
	 *
	 * @param {WebServices.Favorites.Inquiry.Data} updated - The updated favorites data to replace the existing favorites.
	 * @return {void} No return value.
	 */
	public override_favorites_from_updated(updated: WebServices.Favorites.Inquiry.Data): void {
		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.favorites = updated;
			})
		);
	}
}
